/** Formatação do board.
 *
 * Módulo separado dos componentes de propósito: um arquivo que exporta função
 * e componente ao mesmo tempo desliga o Fast Refresh do Vite, e a régua fica
 * lenta justamente na tela em que se mexe mais.
 */

/** O que se diz quando a oportunidade não tem valor informado.
 *
 * Separado da formatação porque **não é dinheiro**: quem renderiza precisa
 * saber que veio ausência para não vestir a frase com a tipografia da cifra.
 * Ver `temValor` — os dois andam juntos.
 */
export const SEM_VALOR = "Sem valor informado";

/** Veio número, ou veio ausência?
 *
 * A distinção existia só dentro de `formatarDinheiro`, e o resultado saía como
 * texto: quem chamava aplicava `font-dados` antes de saber o que tinha em mãos,
 * e "Sem valor" virava o elemento mais forte do card — no painel, maior que o
 * título da oportunidade. A mono é para alinhar dígitos (DESIGN.md § 1.4), e
 * ausência não tem dígito nenhum.
 */
export function temValor(centavos: number | null): centavos is number {
  return centavos !== null;
}

/** Dinheiro em fonte de dados, sem centavos.
 *
 * A coluna é lida verticalmente comparando valores (DESIGN.md § 1.4), e o
 * centavo em um card de oitenta e cinco mil só atrapalha o alinhamento.
 */
export function formatarDinheiro(centavos: number | null, moeda: string): string {
  if (centavos === null) return SEM_VALOR;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: moeda,
    maximumFractionDigits: 0,
  }).format(centavos / 100);
}

/** Dia e mês. O ano só aparece quando a data cai fora do ano corrente — em um
 * funil, quase todo prazo é deste ano, e repetir "2026" em cada card gasta
 * espaço sem informar. */
export function formatarData(iso: string, hoje: Date = new Date()): string {
  const data = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(data.getTime())) return iso;

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    ...(data.getFullYear() === hoje.getFullYear() ? {} : { year: "2-digit" }),
  }).format(data);
}

/** A hora do contato marcado, curta: `15h` ou `15h30`.
 *
 * No card a largura é disputada por seis campos, e "15:00" não diz nada que
 * "15h" não diga. O dia não entra: a tarja só aparece quando o contato é hoje.
 */
export function horaDoContato(iso: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return iso;

  const minutos = quando.getMinutes();
  return minutos === 0
    ? `${String(quando.getHours())}h`
    : `${String(quando.getHours())}h${String(minutos).padStart(2, "0")}`;
}

/** Quantos dias a régua mostra. Espelha `DIAS_DA_REGUA` do projetor. */
export const DIAS_DA_REGUA = 14;

/** Abre o mapa de bits que o read model entrega, do mais antigo ao mais recente.
 *
 * Antes isto recebia `days_in_stage` e inventava a régua: os dias parados na
 * etapa viravam silêncio, e tudo antes disso virava sinal. Um negócio há dez
 * dias em Negociação com conversa diária mostrava dez dias de silêncio — a
 * régua acusava abandono na negociação mais viva do funil; e um card movido
 * ontem aparecia todo aceso com o cliente mudo há duas semanas.
 *
 * Agora o bit vem de `timeline_events`: cada dia em que alguém de fato falou
 * com o cliente. Bit 0 é hoje, e a régua é desenhada do passado para o
 * presente — daí a inversão.
 */
export function sinalDosUltimosDias(mapaDeBits: number): boolean[] {
  const bits = Number.isFinite(mapaDeBits) ? Math.max(Math.trunc(mapaDeBits), 0) : 0;
  const recenteParaAntigo = Array.from(
    { length: DIAS_DA_REGUA },
    (_, dia) => (bits & (1 << dia)) !== 0,
  );
  return recenteParaAntigo.reverse();
}

/** Faz quantos dias foi o último contato? `null` quando não houve nenhum na
 * janela — que é diferente de "faz catorze dias", e a tela precisa distinguir. */
export function diasSemContato(mapaDeBits: number): number | null {
  const bits = Number.isFinite(mapaDeBits) ? Math.max(Math.trunc(mapaDeBits), 0) : 0;
  for (let dia = 0; dia < DIAS_DA_REGUA; dia += 1) {
    if ((bits & (1 << dia)) !== 0) return dia;
  }
  return null;
}

/** O que a régua diz, em palavras — para quem usa leitor de tela e para quem
 * para o cursor em cima dela. Fala de **contato**, que é o que ela mede. */
export function descreverContato(mapaDeBits: number): string {
  const dias = diasSemContato(mapaDeBits);
  if (dias === null) return `Sem contato há mais de ${String(DIAS_DA_REGUA)} dias.`;
  if (dias === 0) return "Falou com o cliente hoje.";
  if (dias === 1) return "Último contato ontem.";
  return `Último contato há ${String(dias)} dias.`;
}

/** A mesma coisa, curta, para a linha do rodapé do card.
 *
 * Ali a frase ocupa o lugar do **prazo** e herdava a fonte dele — uma sentença
 * com ponto final em fonte de dados, repetida em cada card recém-criado. Sem
 * ponto final porque não é frase: é rótulo, como a data que ela substitui.
 */
export function resumirSilencio(diasNaEtapa: number): string {
  if (diasNaEtapa <= 0) return "Entrou hoje";
  return `${diasNaEtapa} ${diasNaEtapa === 1 ? "dia" : "dias"} nesta etapa`;
}

/** Formata o que está sendo digitado como dinheiro, sem mudar o que ele vale.
 *
 * A máscara agrupa milhar e aceita até dois decimais depois da vírgula. Ela
 * **não** empurra os dígitos a partir dos centavos, como faz a máscara de
 * caixa eletrônico: ali quem digita `85000` quer dizer `R$ 850,00`, e aqui
 * quer dizer `R$ 85.000`. O campo sempre foi em reais — o marcador de exemplo
 * diz `85000` e o `centavos` abaixo multiplica por cem —, e inverter isso
 * transformaria toda oportunidade cadastrada até hoje em cem vezes menos.
 *
 * A vírgula sobrevive sozinha enquanto se digita: sem isso, escrever "1200,"
 * apagaria a vírgula no mesmo instante e a pessoa não conseguiria chegar aos
 * centavos.
 */
export function mascararMoeda(texto: string): string {
  const soDigitosEVirgula = texto.replace(/[^\d,]/g, "");
  if (soDigitosEVirgula === "") return "";

  // Só a primeira vírgula conta: o resto é engano de digitação, e recusá-lo
  // calado é melhor que produzir um número que ninguém quis.
  const [inteira = "", ...resto] = soDigitosEVirgula.split(",");
  const decimal = resto.join("").slice(0, 2);

  const agrupada = inteira === "" ? "" : Number(inteira).toLocaleString("pt-BR");
  if (!soDigitosEVirgula.includes(",")) return agrupada;
  return `${agrupada},${decimal}`;
}

/** Aceita "85000", "85.000" e "85000,50". A pessoa digita como fala. */
export function centavos(valor: string): number | null {
  const limpo = valor.replace(/\./g, "").replace(",", ".").trim();
  if (limpo === "") return null;
  const numero = Number(limpo);
  if (!Number.isFinite(numero) || numero < 0) return null;
  return Math.round(numero * 100);
}

/** Quantos filtros estão ativos, para o rótulo do botão.
 *
 * Fora do componente e exportada porque é a única parte disto que vale
 * testar: um contador errado mostra "Filtros" limpo com o funil recortado, e
 * a pessoa passa a tarde procurando um card que o filtro escondeu.
 */
export function contarFiltros(soParadas: boolean, etiqueta: string): number {
  return (soParadas ? 1 : 0) + (etiqueta === "" ? 0 : 1);
}
